import { loadStripe } from '@stripe/stripe-js';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FaSpinner, FaTag } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import countries from 'world-countries';
import * as Yup from 'yup';

import { formatPrice } from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { IUser } from '@/interfaces/api/IUser';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { ICreateOrder } from '@/interfaces/api/orders/CreateOrderDto';
import { IOrder } from '@/interfaces/api/orders/IOrder';
import { IProduct, IProductVariant } from '@/interfaces/api/product/IProduct';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';
import { orderDetailsMapper } from '@/utils/orderDetailsMapper';

const stripePromise = loadStripe(
	import.meta.env.VITE_STRIPE_CONNECT_PUBLIC_KEY || '',
);
const validationSchema = Yup.object().shape({
	email: Yup.string()
		.email('Please enter a valid email address')
		.required('Email is required'),
	firstName: Yup.string().required('First name is required'),
	lastName: Yup.string().required('Last name is required'),
	address: Yup.string().required('Address is required'),
	apartment: Yup.string(), // Optional
	city: Yup.string().required('City is required'),
	state: Yup.string().required('State or territory is required'),
	postcode: Yup.string().required('Postcode is required'),
	country: Yup.string().required('Country is required'),
});

const LocalCheckout = () => {
	const navigate = useNavigate();
	const { state, search } = useLocation();
	const [product, setProduct] = useState<IProduct | null>(null);
	const [variant, setVariant] = useState<IProductVariant | null>(null);
	const [paymentEnabled, setPaymentEnabled] = useState(false);
	const [vendor, setVendor] = useState<IUser | null>(null);
	const [isDiscountValid, setIsDiscountValid] = useState(true);
	const [discountError, setDiscountError] = useState('');
	const [loading, setLoading] = useState(true);
	const [paymentInProgress, setPaymentInProgress] = useState(false);
	const { user } = useUser();

	const countryOptions = countries.map((c) => ({
		value: c.name,
		label: `${c.flag} ${c.name.common}`,
	}));

	const {
		finalPrice,
		quantity,
		variantId,
		storeName,
		productId,
		discountCode,
		isCSVHaggle,
		vendorEmail,
	} = state || {};

	const checkIfDiscountCodeValid = async (code: string, variantId: string) => {
		try {
			await haggleApiService.checkDiscountCode(code, variantId);
			setIsDiscountValid(true);
			setDiscountError('');
			return true;
		} catch (error) {
			setIsDiscountValid(false);
			setDiscountError('This discount code has expired or is no longer valid.');
			return false;
		}
	};

	const handleCheckout = async (values: any) => {
		if (!vendor || !vendor.chargesEnabled) return;

		if (discountCode && variantId && isCSVHaggle) {
			const valid = await checkIfDiscountCodeValid(discountCode, variantId);
			if (!valid) return;
		}
		setPaymentInProgress(true);
		try {
			// Calculate pricing details
			const originalPrice = parseFloat(variant?.price ?? '0');
			const discountAmount = originalPrice - parseFloat(finalPrice ?? '0');
			const subtotal = finalPrice * quantity;

			// Prepare comprehensive order details
			const orderDetails = orderDetailsMapper(
				productId,
				variantId,
				isCSVHaggle,
				product,
				variant,
				originalPrice,
				finalPrice,
				discountAmount,
				quantity,
				subtotal,
			);

			const orderInfo = {
				vendorEmail: vendorEmail,
				isVendorPaidUser: !vendor.roles?.includes(UserRole.MerchantEmerald),
				customerId: '',
				customerEmail: values.email,
				customerName: values.firstName + ' ' + values.lastName,
				productId: productId,
				variantId: variantId,
				quantity: quantity,
				discountCodeId: discountCode,
				shippingAddress: values.address + ' ' + values.country,
				inventoryType: isCSVHaggle ? InventoryType.CSV : InventoryType.SHOPIFY,
				orderDetails: orderDetails,
			};
			const order = await placePendingOrder(orderInfo);

			if (order) {
				const stripe = await stripePromise;

				const res = await haggleApiService.createB2BCheckoutSession(
					Math.round(finalPrice * quantity * 100),
					vendor.id,
					order.id,
					storeName,
					discountCode,
					values.email,
				);
				await stripe?.redirectToCheckout({ sessionId: res.sessionId });
				setPaymentInProgress(false);
			} else {
				setPaymentInProgress(false);
				notificationService.error(
					'An error occurred while placing your order. Please try again later.',
				);
			}
		} catch (error) {
			setPaymentInProgress(false);
			notificationService.error(
				'Unable to proceed to payment. Please try again.',
			);
		}
	};

	const placePendingOrder = async (
		orderInfo: ICreateOrder,
	): Promise<IOrder | null> => {
		try {
			return await haggleApiService.createOrder(orderInfo);
		} catch (error) {
			console.error('create order err:', error);
			notificationService.error(
				'An error occurred while placing your order. Please try again later.',
			);
			return null;
		}
	};

	// Redirect based on status query param
	useEffect(() => {
		const queryParams = new URLSearchParams(search);
		const status = queryParams.get('status');
		const sessionId = queryParams.get('session_id');
		const orderId = queryParams.get('orderId');
		const storeName = queryParams.get('store');

		if (status === 'success') {
			if (sessionId && orderId && storeName) {
				haggleApiService
					.getCheckoutSession(sessionId)
					.then(async (session) => {
						const paymentIntentId = session.payment_intent;
						await haggleApiService.updateOrderPayment(
							+orderId,
							paymentIntentId,
							storeName,
							!vendor?.roles?.includes(UserRole.MerchantEmerald),
						);
						notificationService.success(
							'Order placed successfully! Thank you for your purchase.',
						);
						navigate('/');
					})
					.catch((err) => {
						notificationService.error('Error verifying session:' + err);
					});
			} else {
				navigate('/');
			}
		}

		if (status === 'cancelled') {
			navigate('/');
		}
	}, [state, navigate]);

	// Check if the discount code is valid when the component mounts
	useEffect(() => {
		if (discountCode && variantId && isCSVHaggle) {
			checkIfDiscountCodeValid(discountCode, variantId);
		}
	}, [discountCode, variantId, isCSVHaggle]);

	// Get product info
	useEffect(() => {
		if (storeName && productId && variantId) {
			haggleApiService
				.getProductByIdAndStoreName(storeName, productId, isCSVHaggle)
				.then((product) => {
					if (product) {
						setProduct(product);
						setLoading(false);
						setVariant(
							product.variants?.find((v) => v.id === parseInt(variantId)) ||
								null,
						);
					}
				})
				.catch(() => {
					setLoading(false);
				});
		}
	}, [storeName, productId, variantId]);

	// Check if vendor has a valid stripe connect setup
	useEffect(() => {
		if (product && vendorEmail) {
			haggleApiService.checkIfVendorIsValid(vendorEmail).then((vendor) => {
				setVendor(vendor);
				if (vendor.chargesEnabled) {
					setPaymentEnabled(true);
				}
			});
		}
	}, [product, vendorEmail]);

	return loading ? (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<FaSpinner className="text-4xl animate-spin text-gray-600" />
		</div>
	) : (
		<div className="min-h-screen p-6 ">
			{discountError && (
				<div className="bg-red-100 border border-red-400 text-red-700 p-3 rounded mb-4">
					{discountError}
				</div>
			)}
			<div className="mx-auto bg-white rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-3 glassy-shadow-inset-outward">
				<div className="md:col-span-2 p-6">
					<Formik
						initialValues={{
							email: user ? user.username : '',
							newsletter: false,
							firstName: '',
							lastName: '',
							address: '',
							apartment: '',
							city: '',
							state: '',
							postcode: '',
							saveInfo: false,
							country: 'Australia',
						}}
						validationSchema={validationSchema}
						onSubmit={handleCheckout}
					>
						{({ setFieldValue, values }) => (
							<Form>
								<div className="mb-7">
									<h2 className="text-xl font-semibold mb-4">Contact</h2>

									<div className="mb-2">
										<Field
											disabled={!!user}
											name="email"
											type="email"
											placeholder="Enter Email"
											className="glassy-input"
										/>
										<ErrorMessage
											name="email"
											component="div"
											className="text-red-500 text-sm mt-1"
										/>
									</div>

									{/* <label className="flex items-center mb-4">
										<Field type="checkbox" name="newsletter" className="mr-2" />
										Email me with news and offers
									</label> */}
								</div>

								<h2 className="text-xl font-semibold mb-4">Delivery</h2>
								<div className="space-y-4 mb-4">
									<div>
										<label className="mb-2 text-sm leading-5 font-[450]">
											Country
										</label>
										<Select
											name="country"
											placeholder="Select Country"
											options={countryOptions}
											className="basic-multi-select"
											classNamePrefix="select"
											value={countryOptions.find(
												(option) => option.value.common === values.country,
											)}
											onChange={(country) =>
												setFieldValue('country', country?.value.common)
											}
											styles={{
												control: (provided) => ({
													...provided,
													minHeight: '2.75rem',
													border: 'none',
													borderRadius: '25px',
													boxShadow:
														'inset 4px 4px 10px rgba(154,166,206,0.3), inset -4px -4px 10px #ffffff',
													backgroundColor: 'transparent',
													'&:hover': {
														boxShadow:
															'inset 4px 4px 10px rgba(154,166,206,0.3), inset -4px -4px 10px #ffffff',
													},
													'&:focus-within': {
														boxShadow:
															'inset 4px 4px 10px rgba(154,166,206,0.3), inset -4px -4px 10px #ffffff, 0 0 0 3px rgba(59, 130, 246, 0.1)',
													},
												}),
												placeholder: (provided) => ({
													...provided,
													color: '#9CA3AF',
												}),
												singleValue: (provided) => ({
													...provided,
													color: '#374151',
												}),
												indicatorSeparator: () => ({
													display: 'none',
												}),
												dropdownIndicator: (provided) => ({
													...provided,
													color: '#6B7280',
													'&:hover': {
														color: '#374151',
													},
												}),
											}}
										/>
										<ErrorMessage
											name="country"
											component="div"
											className="text-red-500 text-sm mt-1"
										/>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<Field
												name="firstName"
												placeholder="First name"
												className="glassy-input"
											/>
											<ErrorMessage
												name="firstName"
												component="div"
												className="text-red-500 text-sm mt-1"
											/>
										</div>

										<div>
											<Field
												name="lastName"
												placeholder="Last name"
												className="glassy-input"
											/>
											<ErrorMessage
												name="lastName"
												component="div"
												className="text-red-500 text-sm mt-1"
											/>
										</div>
									</div>

									<div>
										<Field
											name="address"
											placeholder="Address"
											className="glassy-input"
										/>
										<ErrorMessage
											name="address"
											component="div"
											className="text-red-500 text-sm mt-1"
										/>
									</div>

									<div>
										<Field
											name="apartment"
											placeholder="Apartment, suite, etc. (optional)"
											className="glassy-input"
										/>
										<ErrorMessage
											name="apartment"
											component="div"
											className="text-red-500 text-sm mt-1"
										/>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<div>
											<Field
												name="city"
												placeholder="City"
												className="glassy-input"
											/>
											<ErrorMessage
												name="city"
												component="div"
												className="text-red-500 text-sm mt-1"
											/>
										</div>

										<div>
											<Field
												name="state"
												placeholder="State/territory"
												className="glassy-input"
											/>
											<ErrorMessage
												name="state"
												component="div"
												className="text-red-500 text-sm mt-1"
											/>
										</div>

										<div>
											<Field
												name="postcode"
												placeholder="Postcode"
												className="glassy-input"
											/>
											<ErrorMessage
												name="postcode"
												component="div"
												className="text-red-500 text-sm mt-1"
											/>
										</div>
									</div>
								</div>

								<button
									type="submit"
									disabled={!paymentEnabled || !isDiscountValid}
									className={`w-full py-3 rounded-[25px] transition flex justify-center items-center ${
										paymentEnabled && isDiscountValid
											? 'bg-blue-600 text-white hover:bg-blue-700'
											: 'bg-gray-300 text-gray-600 cursor-not-allowed'
									}`}
								>
									{paymentInProgress ? (
										<FaSpinner className="text-xl animate-spin text-white mr-2" />
									) : null}
									<span>
										{paymentInProgress ? 'Processing...' : 'Proceed to Payment'}
									</span>
								</button>
								{!paymentEnabled && (
									<p className="text-red-600 text-sm mt-2 text-center">
										The vendor’s payment system is currently unavailable. Please
										try again later or contact support.
									</p>
								)}
							</Form>
						)}
					</Formik>
				</div>

				<div className="p-6">
					{product && (
						<div className="flex justify-between">
							<div className="flex items-center gap-2 space-x-4 mb-4">
								<div className="relative w-16 h-16">
									<img
										src={
											product.images[0]?.src || 'https://via.placeholder.com/60'
										}
										alt={product.title}
										className="w-16 h-16 rounded object-contain"
									/>
									<span className="absolute -top-1 -right-1 bg-gray-700 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
										{quantity}
									</span>
								</div>
								<div>
									<h3 className="font-medium text-sm">{product.title}</h3>
									<p className="text-xs text-gray-500">{variant?.title}</p>
									{discountCode && (
										<div className="flex items-center gap-2 my-1">
											<FaTag />
											<div>
												<p className="text-xs text-gray-500">{discountCode}</p>
												<p className="text-xs text-gray-500">
													{variant &&
														`(-${formatPrice(
															parseFloat(variant.price ?? '0') -
																parseFloat(finalPrice ?? '0'),
														)})`}
												</p>
											</div>
										</div>
									)}
								</div>
							</div>

							<div>
								<p className="text-sm line-through text-gray-500">
									{formatPrice(variant?.price ?? '0')}
								</p>
								<p className="text-sm font-semibold text-black">
									{formatPrice(finalPrice ?? '0')}
								</p>
							</div>
						</div>
					)}

					<div className="text-sm space-y-1 mt-4">
						<div className="flex justify-between">
							<span>Subtotal</span>
							<span>{formatPrice(finalPrice * quantity)}</span>
						</div>
						<div className="flex justify-between">
							<span>Shipping</span>
							<span className="text-gray-500">Calculated at next step</span>
						</div>
						<div className="flex justify-between font-semibold text-black mt-2">
							<span>Total</span>
							<span>{formatPrice(finalPrice * quantity)}</span>
						</div>
						{variant && (
							<div className="flex items-center gap-2 text-sm text-purpleBrand-500 pt-3">
								<FaTag />
								<span className="font-semibold">
									TOTAL SAVINGS
									{formatPrice(
										(parseFloat(variant.price) - parseFloat(finalPrice)) *
											quantity,
									)}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default LocalCheckout;
